// shared-components/lib/email.js

/**
 * Email Service Module - Frontend Version
 *
 * This module handles email functionality by calling Supabase Edge Functions.
 * All sensitive email operations are handled server-side to protect credentials.
 *
 * Required Environment Variables:
 * - VITE_SUPABASE_URL: Your Supabase project URL
 * - VITE_SUPABASE_ANON_KEY: Your Supabase anonymous key
 */

// Custom error types for different failure scenarios
class EmailConfigError extends Error {
  constructor(message) {
    super(message);
    this.name = "EmailConfigError";
    this.userMessage =
      "The email service is not properly configured. Please try again later.";
  }
}

class EmailSendError extends Error {
  constructor(message, originalError) {
    super(message);
    this.name = "EmailSendError";
    this.originalError = originalError;
    this.userMessage =
      "Unable to send your message at this time. Please try again later.";
  }
}

/**
 * Validate that Supabase configuration is present
 */
const validateEmailConfig = () => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseKey) {
    throw new EmailConfigError(
      "Missing Supabase configuration. Please check environment variables.",
    );
  }
};

export { validateEmailConfig };

/**
 * Send a contact form email via Supabase Edge Function
 * @param {Object} formData - The form data
 * @param {string} formData.name - Sender's name
 * @param {string} formData.email - Sender's email
 * @param {string} formData.subject - Email subject
 * @param {string} formData.message - Email message
 * @returns {Promise<Object>} Result object with success status
 */
export const sendContactEmail = async (formData) => {
  try {
    validateEmailConfig();

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    // Call the Supabase Edge Function
    const response = await fetch(`${supabaseUrl}/functions/v1/contact-form`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${supabaseKey}`,
      },
      body: JSON.stringify(formData),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error("Contact form API error:", data);
      throw new EmailSendError(
        data.error || "Failed to send email",
        new Error(`HTTP ${response.status}`),
      );
    }

    return {
      success: true,
      messageId: data.messageId,
    };
  } catch (error) {
    // Add development mode logging
    if (import.meta.env.DEV) {
      console.log("Email Service Error:", {
        errorName: error.name,
        errorMessage: error.message,
        hasSupabaseUrl: !!import.meta.env.VITE_SUPABASE_URL,
        hasSupabaseKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY,
      });
    }

    // Log error details for debugging
    const errorDetails = {
      type: "EmailSendError",
      message: error.message,
      originalError: error,
      timestamp: new Date().toISOString(),
    };
    console.error("Email Service Error:", errorDetails);

    // Re-throw appropriate error
    if (error instanceof EmailConfigError || error instanceof EmailSendError) {
      throw error;
    }

    throw new EmailSendError("Failed to send email notification", error);
  }
};

/**
 * Legacy function to maintain compatibility
 * @deprecated Use sendContactEmail instead
 */
export const sendEmail = sendContactEmail;
